var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { reaction, observable, computed } from "mobx";
import { Persistence, ClearFallbackData, ClearAuthState } from "../Infra/Persistence";
import { NamedRoutes } from "./NamedRoutes";
import { LoginSource } from "healthex-common/heap.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { FeatureFlag, TefcaProvider } from "healthex-common/features.dtos";
import { effectiveQueryParams } from "./queryParams";
import { isOk } from "healthex-common/result";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { AuthTarget, authTargetFromTefcaProvider } from "healthex-common/clear.dtos";
export var ClearInitialScreen;
(function (ClearInitialScreen) {
    ClearInitialScreen["OTP_VERIFY_PASSCODE"] = "OTP_VERIFY_PASSCODE";
    ClearInitialScreen["OTP_LOCKOUT"] = "OTP_LOCKOUT";
    ClearInitialScreen["PATIENT_CONSENT_CLEAR_UPGRADE"] = "PATIENT_CONSENT_CLEAR_UPGRADE";
})(ClearInitialScreen || (ClearInitialScreen = {}));
export class ClearAuthManager {
    constructor(authManager, persistence, routingUIManager, analyticsService, clearService, featureManager, patientService) {
        this.authManager = authManager;
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.analyticsService = analyticsService;
        this.clearService = clearService;
        this.featureManager = featureManager;
        this.patientService = patientService;
        this.state = ClearAuthState.INACTIVE;
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if ((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.CLEAR_IAL2_CALLBACK.routeName) {
                void this.processClearAuthCallback();
            }
        }, { fireImmediately: true, name: "ClearAuthManager_currentRoute" });
        window.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.resetAuthStateIfNeeded();
            }
        });
    }
    get hasCompletedAuth() {
        return this.state === ClearAuthState.SUCCESS || this.state === ClearAuthState.ERROR;
    }
    async redirectToClearLoginPage(callbackHash, source, organizationId, useFallback, sandboxMode, emailAddress, nextPageRoute, initialScreen) {
        this.state = ClearAuthState.IN_PROGRESS;
        const resolvedOrganizationId = typeof organizationId === "function" ? await organizationId() : organizationId;
        const actuallyUseFallback = await this.determineUseFallback(useFallback !== null && useFallback !== void 0 ? useFallback : false);
        this.persistence.setClearFallbackData(actuallyUseFallback ? new ClearFallbackData({ fallbackLastUsedAt: new Date() }) : null);
        console.log(`Using IAL2 verification: ${!actuallyUseFallback}`);
        let authTarget;
        if (actuallyUseFallback) {
            authTarget = AuthTarget.Fallback;
        }
        else if (emailAddress) {
            const provider = await this.patientService.getPatientTefcaProvider(emailAddress, resolvedOrganizationId);
            if (provider === TefcaProvider.HxTest)
                throw new Error("HxTest patients are not supported for CLEAR login");
            authTarget = authTargetFromTefcaProvider(provider);
        }
        else {
            const provider = await this.patientService.getDefaultTefcaProvider();
            if (provider === TefcaProvider.HxTest)
                throw new Error("HxTest patients are not supported for CLEAR login");
            authTarget = authTargetFromTefcaProvider(provider);
        }
        console.log(`Launching CLEAR login with authTarget ${authTarget}`);
        const heapGlobalEventProperties = { ...getHeapGlobalEventProperties(), source, provider: GFIDProvider.CLEAR };
        const { state, url } = await this.clearService.getIAL2Url(this.getRedirectUri(), authTarget, sandboxMode, heapGlobalEventProperties);
        this.setClearAuthData({
            callbackHash,
            state,
            sandboxMode,
            heapGlobalEventProperties,
            authTarget,
            organizationId: resolvedOrganizationId !== null && resolvedOrganizationId !== void 0 ? resolvedOrganizationId : undefined,
            source,
            nextPageRoute,
            initialScreen,
        });
        window.location.href = url;
    }
    static checkAndReturnClearAuthResponse() {
        const clearAuthData = new Persistence().getClearAuthData();
        if (!clearAuthData)
            return null;
        const urlParams = effectiveQueryParams();
        const code = urlParams.get("code");
        const scope = urlParams.get("scope");
        const state = urlParams.get("state");
        if (code && scope && state) {
            return { code, scope, state };
        }
        return null;
    }
    resetAuth() {
        this.state = ClearAuthState.INACTIVE;
        this.persistence.setClearAuthData(null);
    }
    setClearAuthData(clearAuthData) {
        this.persistence.setClearAuthData(clearAuthData);
    }
    getClearAuthData() {
        return this.persistence.getClearAuthData();
    }
    async determineUseFallback(useFallback) {
        const rolloutEnabledFeature = this.featureManager.isFeatureEnabled(FeatureFlag.RO_CLEAR_LOGIN_FALLBACK);
        if (!rolloutEnabledFeature) {
            return false;
        }
        if (useFallback) {
            return true;
        }
        const clearFallbackData = this.persistence.getClearFallbackData();
        if (!clearFallbackData) {
            return false;
        }
        const resetFeature = this.featureManager.isFeatureEnabled(FeatureFlag.CLEAR_LOGIN_FALLBACK_RESET);
        if (resetFeature) {
            const resetDate = this.featureManager.decodeFeatureConfiguration(FeatureFlag.CLEAR_LOGIN_FALLBACK_RESET);
            if (resetDate instanceof Date && resetDate > clearFallbackData.fallbackLastUsedAt) {
                this.persistence.setClearFallbackData(null);
                console.log(`Reset to IAL2 verification`);
                return false;
            }
        }
        return true;
    }
    async processClearAuthCallback() {
        var _a;
        const clearAuthData = this.getClearAuthData();
        if (!clearAuthData) {
            this.handleAuthError();
            return;
        }
        const { state, organizationId, authTarget, sandboxMode, heapGlobalEventProperties, source } = clearAuthData;
        const clearAuthResponse = ClearAuthManager.checkAndReturnClearAuthResponse();
        if (!clearAuthResponse) {
            this.handleAuthError();
            return;
        }
        if (state !== clearAuthResponse.state) {
            this.handleAuthError();
            return;
        }
        try {
            let linkingToken;
            if (source === LoginSource.ACCOUNT_LINKING) {
                linkingToken = (_a = this.persistence.getAccountLinkingData()) === null || _a === void 0 ? void 0 : _a.linkingToken;
            }
            const result = await this.authManager.authenticateByClearIAL2(clearAuthResponse.code, this.getRedirectUri(), organizationId, authTarget !== null && authTarget !== void 0 ? authTarget : AuthTarget.MedAllies, sandboxMode !== null && sandboxMode !== void 0 ? sandboxMode : false, heapGlobalEventProperties, linkingToken);
            if (isOk(result)) {
                this.handleAuthSuccess();
                this.persistence.setAccountLinkingData(null);
            }
            else {
                this.handleAuthError();
            }
        }
        catch (error) {
            console.error("Error authenticating with CLEAR");
            this.handleAuthError();
        }
        window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
    }
    getRedirectUri() {
        return `${window.location.origin}`;
    }
    handleAuthError() {
        this.state = ClearAuthState.ERROR;
    }
    handleAuthSuccess() {
        this.state = ClearAuthState.SUCCESS;
    }
    resetAuthStateIfNeeded() {
        const clearAuthData = this.getClearAuthData();
        if (clearAuthData) {
            if (clearAuthData.initialScreen === ClearInitialScreen.PATIENT_CONSENT_CLEAR_UPGRADE) {
                this.state = ClearAuthState.INACTIVE;
            }
            else {
                this.resetAuth();
            }
        }
    }
}
__decorate([
    observable
], ClearAuthManager.prototype, "state", void 0);
__decorate([
    computed
], ClearAuthManager.prototype, "hasCompletedAuth", null);
